import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Sidebar from "../features/sidebar/Sidebar";
import Header from "../features/header/Header";
import MainContentArea from "../features/contentArea/MainContentArea";
import { useAppSelector } from "../hooks/useAppSelector";
import LoadingSpinner from "../components/ui/feedback/LoadingSpinner";
import { RootState } from "../app/store";
import { getUserPolicies } from "../api/UserAPIUtils";
import { useAppDispatch } from "../hooks/useAppDispatch";
import useLoading from "../hooks/useLoading";

const HomePage: React.FC = () => {
  const loading = useAppSelector((state) => state.ui.loading);
  const [activeComponent, setActiveComponent] = useState("dashboard");
  const [activeAdminComponent, setActiveAdminComponent] = useState<string>("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const uiControl = useLoading();
  const dispatch = useAppDispatch();

  const handleSetActiveComponent = useCallback((component: string) => {
    setActiveComponent(component);
  }, []);

  const handleSetActiveAdminComponent = useCallback((component: string) => {
    setActiveAdminComponent(component);
  }, []);

  const { isAuthenticated, token, userID } = useAppSelector(
    useMemo(
      () => (state: RootState) => ({
        isAuthenticated: state.auth.isAuthenticated,
        token: state.auth.token,
        userID: state.user.id,
      }),
      [],
    ),
  );

  const fetchPolicies = useCallback(async () => {
    if (userID && token) {
      try {
        uiControl.startLoading();
        await dispatch(getUserPolicies(userID));
      } catch (error) {
        console.error("Error fetching user policies:", error);
      } finally {
        uiControl.stopLoading();
      }
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  return (
    <>
      {loading && <LoadingSpinner />}
      <div className="flex h-screen bg-[linear-gradient(180deg,#f8fbfb_0%,#f1f5f9_100%)]">
        <Sidebar
          setActiveComponent={handleSetActiveComponent}
          setActiveAdminComponent={handleSetActiveAdminComponent}
          activeComponent={activeComponent}
          activeAdminComponent={activeAdminComponent}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header
            setActiveComponent={setActiveComponent}
            activeComponent={activeComponent}
            onOpenMobileSidebar={() => setSidebarOpen(true)}
          />
          <main className="flex-1 overflow-auto">
            <MainContentArea
              activeComponent={activeComponent}
              setActiveComponent={setActiveComponent}
              activeAdministrationComponent={activeAdminComponent}
            />
          </main>
        </div>
      </div>
    </>
  );
};

export default HomePage;
